import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Check, Clock, Award, Heart, Menu, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { useNavigate } from 'react-router-dom';
import { useLanguage, getPageTexts } from '../i18n/pageTranslations';

const NavigationCard = ({ title, imageUrl, link, description }) => {
  const navigate = useNavigate();
  
  const handleClick = () => {
    if (link) {
      navigate(link);
      window.scrollTo(0, 0);
    }
  };
  
  return (
    <Card 
      onClick={handleClick}
      data-testid={`nav-card-${link?.replace(/\//g, '-')}`}
      className="relative h-64 overflow-hidden cursor-pointer group hover:shadow-2xl transition-all duration-500 border-none rounded-none"
    >
      <div className="absolute inset-0">
        <img 
          src={imageUrl} 
          alt={title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#3d5a6f]/70 via-[#3d5a6f]/60 to-[#1C1C1A]/80"></div>
      </div>
      <div className="relative h-full flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-0.5 bg-ivory/60 mb-4"></div>
        <h4 className="text-lg md:text-xl font-light text-white uppercase tracking-wide leading-tight">
          {title}
        </h4>
        {description && (
          <p className="text-white/70 text-sm mt-2">{description}</p>
        )}
        <div className="w-16 h-0.5 bg-ivory/60 mt-4"></div>
      </div>
    </Card>
  );
};

const Rinomodelacion = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const lang = useLanguage();
  const t = getPageTexts('rinomodelacion', lang);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const menuItemIds = ['inicio', 'secciones', 'que-es', 'doctor', 'contacto'];

  // Tarjetas de navegación principales
  const mainSections = [
    { 
      title: t.sections?.[0]?.title || 'Rinomodelación ¿Qué es?', 
      image: 'https://images.pexels.com/photos/7585311/pexels-photo-7585311.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', 
      link: '/rinomodelacion/que-es',
      description: t.sections?.[0]?.desc || 'Todo sobre el procedimiento'
    },
    { 
      title: t.sections?.[1]?.title || 'Resultados Antes y Después', 
      image: 'https://images.pexels.com/photos/5659022/pexels-photo-5659022.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', 
      link: '/rinomodelacion/resultados',
      description: t.sections?.[1]?.desc || 'Galería de casos reales'
    },
    { 
      title: t.sections?.[2]?.title || 'Dr. Ángel Ruiz Norro', 
      image: 'https://images.pexels.com/photos/5699456/pexels-photo-5699456.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', 
      link: '/rinomodelacion/doctor',
      description: t.sections?.[2]?.desc || 'Conozca al especialista'
    },
    { 
      title: t.sections?.[3]?.title || 'Preguntas Frecuentes', 
      image: 'https://images.pexels.com/photos/7108257/pexels-photo-7108257.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', 
      link: '/rinomodelacion/faq',
      description: t.sections?.[3]?.desc || 'Resolvemos tus dudas'
    },
    { 
      title: t.sections?.[4]?.title || 'Contacto', 
      image: 'https://images.pexels.com/photos/3825527/pexels-photo-3825527.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', 
      link: '/rinomodelacion/contacto',
      description: t.sections?.[4]?.desc || 'Solicite su valoración'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="rinomodelacion-page">
      <Header />
      
      {/* Internal Navigation Menu */}
      <nav className="sticky top-20 z-40 bg-ivory shadow-md">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="hidden md:flex space-x-6 overflow-x-auto">
              {t.menuItems?.map((item, index) => (
                <button
                  key={menuItemIds[index]}
                  onClick={() => scrollToSection(menuItemIds[index])}
                  className="text-[#1C1C1A] hover:text-gold whitespace-nowrap transition-colors font-medium"
                  data-testid={`menu-${menuItemIds[index]}`}
                >
                  {item}
                </button>
              ))}
            </div>
            
            <button
              className="md:hidden text-[#1C1C1A]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
          
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t">
              {t.menuItems?.map((item, index) => (
                <button
                  key={menuItemIds[index]}
                  onClick={() => scrollToSection(menuItemIds[index])}
                  className="block w-full text-left py-2 text-[#1C1C1A] hover:text-gold transition-colors"
                >
                  {item}
                </button>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section - Inicio */}
      <section id="inicio" className="relative pt-16 pb-20 px-4 bg-gradient-to-b from-cream to-white">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-8">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-light text-[#1C1C1A] mb-6 leading-tight">
              {t.title}
            </h1>
            <p className="text-2xl md:text-3xl text-text-secondary mb-4">
              {t.heroSubtitle}
            </p>
            <p className="text-xl text-text-secondary mb-8">
              {t.heroDesc}
            </p>
            
            <Button 
              onClick={() => navigate('/rinomodelacion/contacto')}
              data-testid="cta-estudio-gratuito"
              className="bg-gold hover:bg-gold text-white px-8 py-6 text-lg rounded transition-all duration-300"
            >
              {t.requestStudy}
            </Button>
          </div>
        </div>
      </section>

      {/* Main Navigation Cards - Secciones */}
      <section id="secciones" className="py-20 px-4 bg-cream">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl md:text-5xl font-light text-[#1C1C1A] mb-4 text-center">
            {t.sectionsTitle}
          </h2>
          <p className="text-text-secondary text-center mb-12">
            {t.sectionsSubtitle}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {mainSections.map((section, index) => (
              <NavigationCard 
                key={index} 
                title={section.title} 
                imageUrl={section.image} 
                link={section.link}
                description={section.description}
              />
            ))}
          </div>
        </div>
      </section>

      {/* What is Rinomodelación - Preview */}
      <section id="que-es" className="py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-4xl md:text-5xl font-light text-[#1C1C1A] mb-8 text-center">
            {t.whatIsTitle}
          </h2>
          
          <div className="prose max-w-none mb-8">
            <p className="text-lg text-text-secondary leading-relaxed mb-6 text-center">
              {t.whatIsDesc}
            </p>
          </div>

          <div className="bg-ivory p-8 rounded-none shadow-md mb-8">
            <h3 className="text-2xl font-light text-[#1C1C1A] mb-6 text-center">
              {t.advantagesTitle}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <Clock className="text-gold mt-1 flex-shrink-0" size={24} />
                <div>
                  <h4 className="font-semibold text-[#1C1C1A] mb-2">{t.adv1Title}</h4>
                  <p className="text-text-secondary">{t.adv1Desc}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Award className="text-gold mt-1 flex-shrink-0" size={24} />
                <div>
                  <h4 className="font-semibold text-[#1C1C1A] mb-2">{t.adv2Title}</h4>
                  <p className="text-text-secondary">{t.adv2Desc}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Heart className="text-gold mt-1 flex-shrink-0" size={24} />
                <div>
                  <h4 className="font-semibold text-[#1C1C1A] mb-2">{t.adv3Title}</h4>
                  <p className="text-text-secondary">{t.adv3Desc}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Check className="text-gold mt-1 flex-shrink-0" size={24} />
                <div>
                  <h4 className="font-semibold text-[#1C1C1A] mb-2">{t.adv4Title}</h4>
                  <p className="text-text-secondary">{t.adv4Desc}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Button 
              onClick={() => navigate('/rinomodelacion/que-es')}
              className="bg-gold hover:bg-gold text-white px-8 py-3"
              data-testid="btn-saber-mas-que-es"
            >
              {t.learnMore}
            </Button>
          </div>
        </div>
      </section>

      {/* Results Preview */}
      <section className="py-20 px-4 bg-cream">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-4xl md:text-5xl font-light text-[#1C1C1A] mb-8 text-center">
            {t.resultsTitle}
          </h2>
          <h3 className="text-2xl text-text-secondary mb-12 text-center">
            {t.resultsSubtitle}
          </h3>
          
          <div className="bg-gradient-to-r from-cream0 to-blue-500 text-white p-8 rounded-none mb-12 text-center">
            <h3 className="text-3xl font-light mb-4">{t.casesTitle}</h3>
            <p className="text-lg mb-6">
              {t.casesDesc}
            </p>
            <Button 
              onClick={() => navigate('/rinomodelacion/resultados')}
              className="bg-ivory text-gold hover:bg-gray-100 px-8 py-3"
              data-testid="btn-ver-resultados"
            >
              {t.viewGallery}
            </Button>
          </div>
        </div>
      </section>

      {/* Doctor Section */}
      <section id="doctor" className="py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-4xl md:text-5xl font-light text-[#1C1C1A] mb-8 text-center">
            {t.doctorTitle}
          </h2>

          <div className="bg-gradient-to-r from-cream to-blue-50 p-8 rounded-none mb-8">
            <p className="text-lg text-text-secondary italic mb-6 leading-relaxed text-center">
              {t.doctorQuote}
            </p>
            <p className="text-center text-[#1C1C1A] font-semibold text-xl">
              — Dr. Ángel Ruiz Norro
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-cream p-6 rounded-none">
              <h5 className="font-semibold text-[#1C1C1A] mb-3">{t.doctorExp1}</h5>
              <p className="text-text-secondary">{t.doctorExp1Desc}</p>
            </div>
            
            <div className="bg-cream p-6 rounded-none">
              <h5 className="font-semibold text-[#1C1C1A] mb-3">{t.doctorExp2}</h5>
              <p className="text-text-secondary">{t.doctorExp2Desc}</p>
            </div>
          </div>

          <div className="text-center">
            <Button 
              onClick={() => navigate('/rinomodelacion/doctor')}
              className="bg-gold hover:bg-gold text-white px-8 py-3"
              data-testid="btn-conocer-doctor"
            >
              {t.meetDoctor}
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-20 px-4 bg-[#1C1C1A] text-white">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-light mb-6">
            {t.contactTitle}
          </h2>
          <p className="text-lg text-white/80 mb-6">
            {t.clinicName}
          </p>
          
          <div className="bg-ivory/10 p-6 rounded-none mb-8">
            <div className="space-y-3 text-lg">
              <p><strong>{t.address}:</strong> C/ Carlos Mackintosh, 3 - 29602 Marbella</p>
              <p>Teléfono: 952 76 45 47</p>
              <p>WhatsApp: 696 345 792</p>
              <p>Email: info@rinomodelacion.com</p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/rinomodelacion/contacto')}
              className="bg-gold hover:bg-gold text-white px-8 py-6 text-lg rounded"
              data-testid="btn-solicitar-consulta"
            >
              {t.requestConsult}
            </Button>
            <Button 
              onClick={() => window.open('https://wa.me/34696345792', '_blank')}
              className="bg-gold500 hover:bg-gold600 text-white px-8 py-6 text-lg rounded"
              data-testid="btn-whatsapp"
            >
              WHATSAPP
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Rinomodelacion;
